import React from 'react'

import { Box } from 'ui/components/Box'
import { DropdownItemComplex } from 'ui/components/Dropdown'
import { Field } from 'ui/components/Field'
import { Input } from 'ui/components/Input'
import { Select, SelectOption, SelectSeparator, SelectTitle } from 'ui/components/Select'
import { Toggle } from 'ui/components/Toggle'

import { useNavigateToSettingsState } from './hooks/useNavigateToSettingsState'

type NavigateToSettingsProps = {
    object: ObjectDto
    step: ActionStep
    onChange: (patch: Partial<ActionStep>) => void
}

export const NavigateToSettings: React.FC<NavigateToSettingsProps> = ({
    object,
    step,
    onChange,
}) => {
    const {
        linkType,
        onChangeLinkType,
        linkHref,
        onChangeLinkHref,
        linkOpenAs,
        onChangeLinkOpenAs,
        linkFieldApiName,
        onChangeFieldApiName,
        supportedFields,
        internalLinks,
        onChangeInternalLink,
        internalLinksValue,
    } = useNavigateToSettingsState({
        step,
        object,
        onChange,
    })

    return (
        <Box flex column gap="l">
            <Select label="Link type" value={linkType} onChange={onChangeLinkType}>
                <SelectOption label="Internal" value="view" />
                <SelectOption label="External" value="url" />
                <SelectOption label="From Field" value="field" />
            </Select>
            {linkType === 'field' && (
                <Select
                    placeholder="Select field..."
                    label="Field"
                    isSearchable
                    value={linkFieldApiName}
                    onChange={onChangeFieldApiName}
                >
                    {supportedFields.map((field) => (
                        <SelectOption
                            key={field.api_name}
                            value={field.api_name}
                            label={field.label}
                        />
                    ))}
                </Select>
            )}
            {linkType === 'url' && (
                <Input
                    label="Link URL"
                    value={linkHref}
                    onChange={onChangeLinkHref}
                    placeholder="https://..."
                />
            )}
            {linkType === 'view' && (
                <Select
                    label="Link target"
                    placeholder="Select target..."
                    isSearchable
                    value={internalLinksValue}
                    onChange={onChangeInternalLink}
                >
                    {internalLinks.map((section, idx) => [
                        <SelectTitle key={section.id}>{section.label}</SelectTitle>,
                        ...section.children.map((child) => (
                            <SelectOption
                                as={DropdownItemComplex}
                                key={child.id}
                                value={child.id}
                                label={child.label}
                                subtitle={child.viewTypeLabel}
                            />
                        )),
                        idx < internalLinks.length - 1 && (
                            <SelectSeparator key={`separator-${section.id}`} />
                        ),
                    ])}
                </Select>
            )}
            <Field
                htmlFor="linkOpenAs"
                label="Open link in new tab"
                rightSlotContent={
                    <Toggle
                        id="linkOpenAs"
                        checked={linkOpenAs}
                        onCheckedChange={onChangeLinkOpenAs}
                    />
                }
            />
        </Box>
    )
}
